/**
 * DryRunPreview - Renders the full dry-run report from the preview pipeline.
 *
 * Shows MusicDNA, Visual Style, Scene Breakdown, Model Routing decisions,
 * API payloads, and cost summary — everything that would happen before
 * calling the external video API.
 */

import { useState } from 'react';
import {
  Music, Palette, Film, Cpu, DollarSign, AlertTriangle,
  ChevronDown, ChevronRight, Eye, Zap, Clock,
} from 'lucide-react';

interface ScenePreview {
  job_id?: string;
  scene_num?: number;
  selected_model?: string;
  model_provider?: string;
  routing_confidence?: number;
  routing_reasons?: string[];
  fallback_model?: string | null;
  api_endpoint?: string;
  api_payload?: Record<string, unknown>;
  effective_duration?: number;
  duration_was_clamped?: boolean;
  max_model_duration?: number;
  estimated_cost_usd?: number;
  all_model_scores?: Array<{
    model_id?: string;
    score?: number;
    reasons?: string[];
    provider?: string;
  }>;
  model_strengths?: string[];
  model_weaknesses?: string[];
  fallback_payload?: Record<string, unknown> | null;
  error?: string;
}

interface DryRunReport {
  music_dna?: Record<string, unknown>;
  visual_style_guide?: Record<string, unknown>;
  scenes?: Array<Record<string, unknown>>;
  video_gen_queue?: Array<Record<string, unknown>>;
  scene_previews?: ScenePreview[];
  total_estimated_cost_usd?: number;
  total_effective_duration?: number;
  constraint_warnings?: string[];
}

interface DryRunPreviewProps {
  report: DryRunReport;
  onProceed: () => void;
  onBack: () => void;
}

function SectionHeader({ icon: Icon, title, badge }: { icon: React.ComponentType<{ className?: string }>; title: string; badge?: string }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <Icon className="w-4 h-4 text-jelly-400" />
      <h3 className="font-medium">{title}</h3>
      {badge && (
        <span className="ml-auto text-xs px-2 py-0.5 rounded-full bg-zinc-700 text-zinc-300">{badge}</span>
      )}
    </div>
  );
}

function CollapsibleJson({ data, label }: { data: unknown; label: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-zinc-700 rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-3 py-2 text-xs text-zinc-400 hover:bg-zinc-800/50 transition-colors"
      >
        {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        {label}
      </button>
      {open && (
        <pre className="px-3 pb-3 text-xs text-zinc-400 overflow-x-auto max-h-64 overflow-y-auto whitespace-pre-wrap break-all">
          {JSON.stringify(data, null, 2)}
        </pre>
      )}
    </div>
  );
}

function ConfidenceBadge({ confidence }: { confidence: number }) {
  const pct = Math.round(confidence * 100);
  const color = pct >= 80 ? 'text-green-400 bg-green-400/10' :
                pct >= 60 ? 'text-amber-400 bg-amber-400/10' :
                'text-red-400 bg-red-400/10';
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${color}`}>
      {pct}%
    </span>
  );
}

export default function DryRunPreview({ report, onProceed, onBack }: DryRunPreviewProps) {
  const [expandedScene, setExpandedScene] = useState<number | null>(null);

  const musicDna = report.music_dna || {};
  const visualStyle = report.visual_style_guide || {};
  const scenes = report.scenes || [];
  const scenePreviews = report.scene_previews || [];
  const totalCost = report.total_estimated_cost_usd || 0;
  const totalDuration = report.total_effective_duration || 0;
  const warnings = report.constraint_warnings || [];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="text-center py-3">
        <div className="inline-flex items-center gap-2 bg-purple-500/10 border border-purple-500/20 rounded-full px-4 py-2 mb-2">
          <Eye className="w-4 h-4 text-purple-400" />
          <span className="text-sm font-medium text-purple-300">Dry Run Preview</span>
        </div>
        <p className="text-xs text-zinc-400">
          Full pipeline ran without calling video APIs. Review what would be generated.
        </p>
      </div>

      {/* Constraint Warnings */}
      {warnings.length > 0 && (
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-3">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-amber-400" />
            <span className="text-sm font-medium text-amber-300">Platform Constraints</span>
          </div>
          <ul className="space-y-1">
            {warnings.map((w, i) => (
              <li key={i} className="text-xs text-amber-400/80">{w}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Cost & Duration Summary */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-zinc-800/50 rounded-xl p-3 text-center">
          <DollarSign className="w-4 h-4 text-green-400 mx-auto mb-1" />
          <div className="text-lg font-bold text-green-400">
            {totalCost === 0 ? 'Free' : `$${totalCost.toFixed(4)}`}
          </div>
          <div className="text-xs text-zinc-500">Estimated Cost</div>
        </div>
        <div className="bg-zinc-800/50 rounded-xl p-3 text-center">
          <Clock className="w-4 h-4 text-blue-400 mx-auto mb-1" />
          <div className="text-lg font-bold text-blue-400">
            {totalDuration.toFixed(1)}s
          </div>
          <div className="text-xs text-zinc-500">Total Duration</div>
        </div>
      </div>

      {/* Music DNA */}
      <div className="bg-zinc-800/50 rounded-xl p-4">
        <SectionHeader icon={Music} title="Music DNA" />
        <div className="space-y-2">
          {/* Genre + Mood */}
          <div className="flex flex-wrap gap-2">
            {musicDna.genre ? (
              <span className="px-2 py-1 rounded-full bg-purple-500/20 text-purple-300 text-xs font-medium">
                {String(musicDna.genre)}
              </span>
            ) : null}
            {(musicDna.mood_tags as unknown[] | undefined)?.map((tag: unknown, i: number) => (
              <span key={i} className="px-2 py-1 rounded-full bg-pink-500/10 text-pink-300 text-xs">
                {typeof tag === 'string' ? tag : JSON.stringify(tag)}
              </span>
            ))}
          </div>
          {/* BPM, Key, Energy */}
          <div className="flex gap-4 text-xs text-zinc-400">
            {musicDna.bpm ? <span>BPM: {String(musicDna.bpm)}</span> : null}
            {musicDna.key ? <span>Key: {String(musicDna.key)}</span> : null}
            {musicDna.energy != null ? <span>Energy: {String(musicDna.energy)}</span> : null}
          </div>
          {/* Instruments */}
          {(musicDna.instruments as unknown[] | undefined)?.length ? (
            <div className="flex flex-wrap gap-1">
              {(musicDna.instruments as unknown[]).map((inst: unknown, i: number) => (
                <span key={i} className="px-2 py-0.5 rounded bg-zinc-700 text-zinc-300 text-xs">
                  {typeof inst === 'string' ? inst : JSON.stringify(inst)}
                </span>
              ))}
            </div>
          ) : null}
          {/* Similar Artists */}
          {(musicDna.similar_artists as Array<{ name?: string; visual_keywords?: string[] }> | undefined)?.length && (
            <div className="mt-2 pt-2 border-t border-zinc-700">
              <p className="text-xs text-zinc-500 mb-1">Similar Artists</p>
              <div className="space-y-1">
                {(musicDna.similar_artists as Array<{ name?: string; visual_keywords?: string[] }>).map((a, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="text-xs font-medium text-zinc-300">{a.name}</span>
                    {a.visual_keywords && (
                      <span className="text-xs text-zinc-500">
                        {a.visual_keywords.slice(0, 3).join(', ')}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          <CollapsibleJson data={musicDna} label="Full Music DNA JSON" />
        </div>
      </div>

      {/* Visual Style */}
      <div className="bg-zinc-800/50 rounded-xl p-4">
        <SectionHeader icon={Palette} title="Visual Style Guide" />
        <div className="space-y-2">
          {/* Color Palette */}
          {(visualStyle.color_palette as unknown[] | undefined)?.length ? (
            <div>
              <p className="text-xs text-zinc-500 mb-1">Color Palette</p>
              <div className="flex flex-wrap gap-2">
                {(visualStyle.color_palette as unknown[]).map((color: unknown, i: number) => {
                  // Handle both string colors ("#D4A574") and object colors ({hex, name, emotion, usage})
                  const isObj = typeof color === 'object' && color !== null;
                  const hex = isObj ? (color as Record<string, string>).hex : String(color);
                  const label = isObj ? (color as Record<string, string>).name || hex : hex;
                  const emotion = isObj ? (color as Record<string, string>).emotion : undefined;
                  return (
                    <div key={i} className="flex items-center gap-1">
                      <div
                        className="w-5 h-5 rounded border border-zinc-600"
                        style={{ backgroundColor: hex }}
                      />
                      <div className="text-xs">
                        <span className="text-zinc-300">{label}</span>
                        {emotion && <span className="text-zinc-500 ml-1">({emotion})</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}
          {/* Lighting, Camera, Settings */}
          <div className="grid grid-cols-2 gap-2 text-xs">
            {visualStyle.lighting ? (
              <div>
                <span className="text-zinc-500">Lighting: </span>
                <span className="text-zinc-300">{typeof visualStyle.lighting === 'string' ? visualStyle.lighting : JSON.stringify(visualStyle.lighting)}</span>
              </div>
            ) : null}
            {visualStyle.camera_style ? (
              <div>
                <span className="text-zinc-500">Camera: </span>
                <span className="text-zinc-300">{typeof visualStyle.camera_style === 'string' ? visualStyle.camera_style : JSON.stringify(visualStyle.camera_style)}</span>
              </div>
            ) : null}
            {visualStyle.setting ? (
              <div>
                <span className="text-zinc-500">Setting: </span>
                <span className="text-zinc-300">{typeof visualStyle.setting === 'string' ? visualStyle.setting : JSON.stringify(visualStyle.setting)}</span>
              </div>
            ) : null}
            {visualStyle.visual_tone ? (
              <div>
                <span className="text-zinc-500">Tone: </span>
                <span className="text-zinc-300">{typeof visualStyle.visual_tone === 'string' ? visualStyle.visual_tone : JSON.stringify(visualStyle.visual_tone)}</span>
              </div>
            ) : null}
          </div>
          <CollapsibleJson data={visualStyle} label="Full Visual Style JSON" />
        </div>
      </div>

      {/* Scene Breakdown + Model Routing */}
      <div className="bg-zinc-800/50 rounded-xl p-4">
        <SectionHeader icon={Film} title="Scene Breakdown" badge={`${scenePreviews.length} scenes`} />
        <div className="space-y-3">
          {scenePreviews.map((preview, idx) => {
            const sceneData = scenes[idx] || {};
            const queueItem = (report.video_gen_queue || [])[idx] || {};
            const isExpanded = expandedScene === idx;

            return (
              <div key={idx} className="border border-zinc-700 rounded-lg overflow-hidden">
                {/* Scene Header */}
                <button
                  onClick={() => setExpandedScene(isExpanded ? null : idx)}
                  className="w-full flex items-center gap-3 px-3 py-2 hover:bg-zinc-800/50 transition-colors"
                >
                  <span className="text-xs font-bold text-zinc-400 w-8">#{(preview.scene_num || idx + 1)}</span>
                  <span className="flex-1 text-left text-sm text-zinc-300 truncate">
                    {(queueItem.prompt as string)?.slice(0, 80) || `Scene ${idx + 1}`}
                  </span>
                  <div className="flex items-center gap-2">
                    {preview.estimated_cost_usd != null && (
                      <span className="text-xs text-green-400">
                        ${preview.estimated_cost_usd.toFixed(4)}
                      </span>
                    )}
                    {preview.routing_confidence != null && (
                      <ConfidenceBadge confidence={preview.routing_confidence} />
                    )}
                    {preview.selected_model && (
                      <span className="text-xs px-2 py-0.5 rounded bg-blue-500/10 text-blue-300">
                        {preview.selected_model}
                      </span>
                    )}
                  </div>
                  {isExpanded ? <ChevronDown className="w-4 h-4 text-zinc-500" /> : <ChevronRight className="w-4 h-4 text-zinc-500" />}
                </button>

                {/* Expanded Details */}
                {isExpanded && (
                  <div className="px-3 pb-3 space-y-3 border-t border-zinc-700 pt-3">
                    {/* Error */}
                    {preview.error && (
                      <div className="text-xs text-red-400 bg-red-500/10 rounded p-2">
                        Error: {preview.error}
                      </div>
                    )}

                    {/* Prompt */}
                    <div>
                      <p className="text-xs text-zinc-500 mb-1">Prompt</p>
                      <p className="text-xs text-zinc-300 bg-zinc-900 rounded p-2 whitespace-pre-wrap">
                        {(queueItem.prompt as string) || 'N/A'}
                      </p>
                    </div>
                    {queueItem.negative_prompt ? (
                      <div>
                        <p className="text-xs text-zinc-500 mb-1">Negative Prompt</p>
                        <p className="text-xs text-zinc-400 bg-zinc-900 rounded p-2 whitespace-pre-wrap">
                          {String(queueItem.negative_prompt)}
                        </p>
                      </div>
                    ) : null}

                    {/* Scene metadata */}
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div>
                        <span className="text-zinc-500">Duration: </span>
                        <span className="text-zinc-300">{preview.effective_duration || 0}s</span>
                        {preview.duration_was_clamped && (
                          <span className="ml-1 text-amber-400">(clamped)</span>
                        )}
                      </div>
                      <div>
                        <span className="text-zinc-500">Max: </span>
                        <span className="text-zinc-300">{preview.max_model_duration || 0}s</span>
                      </div>
                      <div>
                        <span className="text-zinc-500">Mood: </span>
                        <span className="text-zinc-300">{String((sceneData as Record<string, unknown>).mood || (queueItem as Record<string, unknown>).mood || 'N/A')}</span>
                      </div>
                    </div>

                    {/* Model Routing */}
                    <div className="bg-zinc-900 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <Cpu className="w-3 h-3 text-blue-400" />
                        <span className="text-xs font-medium text-zinc-300">Model Routing</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs mb-2">
                        <div>
                          <span className="text-zinc-500">Selected: </span>
                          <span className="text-blue-300 font-medium">{preview.selected_model || 'N/A'}</span>
                        </div>
                        <div>
                          <span className="text-zinc-500">Provider: </span>
                          <span className="text-zinc-300">{preview.model_provider || 'N/A'}</span>
                        </div>
                        {preview.fallback_model && (
                          <div className="col-span-2">
                            <span className="text-zinc-500">Fallback: </span>
                            <span className="text-amber-300">{preview.fallback_model}</span>
                          </div>
                        )}
                      </div>

                      {/* Routing Reasons */}
                      {preview.routing_reasons && preview.routing_reasons.length > 0 && (
                        <div className="mb-2">
                          <p className="text-xs text-zinc-500 mb-1">Routing Reasons</p>
                          <div className="flex flex-wrap gap-1">
                            {preview.routing_reasons.map((reason, i) => (
                              <span key={i} className="px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-400 text-[10px]">
                                {reason}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Strengths / Weaknesses */}
                      <div className="grid grid-cols-2 gap-2">
                        {preview.model_strengths && preview.model_strengths.length > 0 && (
                          <div>
                            <p className="text-[10px] text-green-400 mb-0.5">Strengths</p>
                            {preview.model_strengths.map((s, i) => (
                              <span key={i} className="block text-[10px] text-zinc-400">{s}</span>
                            ))}
                          </div>
                        )}
                        {preview.model_weaknesses && preview.model_weaknesses.length > 0 && (
                          <div>
                            <p className="text-[10px] text-red-400 mb-0.5">Weaknesses</p>
                            {preview.model_weaknesses.map((w, i) => (
                              <span key={i} className="block text-[10px] text-zinc-400">{w}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* All Model Scores */}
                    {preview.all_model_scores && preview.all_model_scores.length > 0 && (
                      <div>
                        <p className="text-xs text-zinc-500 mb-1">All Model Scores</p>
                        <div className="space-y-1 max-h-32 overflow-y-auto">
                          {preview.all_model_scores.map((ms, i) => (
                            <div key={i} className="flex items-center gap-2 text-[10px]">
                              <span className="font-mono w-8 text-right text-zinc-400">{(ms.score || 0).toFixed(0)}</span>
                              <div className="flex-1 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-blue-500/50 rounded-full"
                                  style={{ width: `${Math.min(100, ((ms.score || 0) / (preview.all_model_scores![0]?.score || 1)) * 100)}%` }}
                                />
                              </div>
                              <span className={`w-32 truncate ${i === 0 ? 'text-blue-300 font-medium' : 'text-zinc-500'}`}>
                                {ms.model_id}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* API Payload */}
                    <CollapsibleJson data={preview.api_payload} label={`API Payload (${preview.api_endpoint || 'endpoint'})`} />
                    {preview.fallback_payload && (
                      <CollapsibleJson data={preview.fallback_payload} label="Fallback Payload" />
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer Actions */}
      <div className="flex gap-3 pt-2">
        <button
          onClick={onBack}
          className="flex-1 px-4 py-2.5 rounded-lg border border-zinc-700 text-zinc-300 hover:bg-zinc-800 transition-colors text-sm"
        >
          Back to Setup
        </button>
        <button
          onClick={onProceed}
          className="flex-1 px-4 py-2.5 rounded-lg bg-gradient-to-r from-jelly-500 to-purple-500 text-white font-medium hover:opacity-90 transition-opacity text-sm flex items-center justify-center gap-2"
        >
          <Zap className="w-4 h-4" />
          Proceed to Generate
        </button>
      </div>

      {/* Scene Data JSON */}
      <CollapsibleJson data={report} label="Full Dry Run Report JSON" />
    </div>
  );
}
